import React, { useEffect, useState } from "react";
import NewsItem from "../components/NewsItem"; // Component to render individual news articles
// import AdBanner from "../components/AdBanners"; // Component to render ads

// Backend API URL from environment variables
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const Health = ({ searchQuery }) => {
  // State to store all fetched health news
  const [news, setNews] = useState([]);
  // State to store news filtered by search query
  const [filteredNews, setFilteredNews] = useState([]);
  // Loading state for initial fetch
  const [loading, setLoading] = useState(true);
  // Tracks whether this is the first load
  const [initialLoad, setInitialLoad] = useState(true);
  // Current pagination page
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5; // Number of posts per page

  /**
   * Fetch health news from backend and cache in localStorage
   * Uses cached data if available to reduce API calls
   */
  useEffect(() => {
    const cachedNews = localStorage.getItem("cachedHealthNews");
    let parsedCache = [];

    if (cachedNews) {
      parsedCache = JSON.parse(cachedNews);
      setNews(parsedCache);
      setFilteredNews(parsedCache);
      setLoading(false);
      setInitialLoad(false);
    }

    // Fetch latest news from backend
    fetch(`${backendUrl}/api/news/health`)
      .then((res) => res.json())
      .then((fetchedData) => {
        const cachedString = JSON.stringify(parsedCache);
        const fetchedString = JSON.stringify(fetchedData);

        // Only update state & cache if data differs from cached version
        if (cachedString !== fetchedString) {
          setNews(fetchedData);
          setFilteredNews(fetchedData);
          localStorage.setItem("cachedHealthNews", fetchedString);
        }
      })
      .catch((err) => {
        console.error("Error fetching health news:", err);
      })
      .finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
  }, []);

  /**
   * Filter news based on search query
   * Updates filteredNews whenever searchQuery or news array changes
   */
  useEffect(() => {
    if (!searchQuery) {
      setFilteredNews(news);
    } else {
      const filtered = news.filter((article) =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredNews(filtered);
    }
  }, [searchQuery, news]);

  // Calculate pagination indexes
  const lastPostIndex = currentPage * postsPerPage;
  const firstPostIndex = lastPostIndex - postsPerPage;
  const currentPosts = filteredNews.slice(firstPostIndex, lastPostIndex);
  const totalPages = Math.ceil(filteredNews.length / postsPerPage);

  /**
   * Effect to update Date & Time widget every second
   * Populates elements with ids: widget-date & widget-time
   */
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const dateOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
      const timeOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };

      const dateEl = document.getElementById("widget-date");
      const timeEl = document.getElementById("widget-time");

      if (dateEl && timeEl) {
        dateEl.textContent = now.toLocaleDateString("en-US", dateOptions);
        timeEl.textContent = now.toLocaleTimeString("en-US", timeOptions);
      }
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="container mx-auto px-4 mt-6 lg:flex gap-6">
      {/* Left Column - Health Feed */}
      <div className="lg:w-8/12 w-full mb-5 mt-5">
        {initialLoad && loading ? (
          // Show loading spinner during initial fetch
          <div className="flex justify-center items-center mt-10">
            <div className="flex justify-center items-center w-screen bg-gray-50">
              <div className="w-5 h-5 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : (
          <>
            {currentPosts.length > 0 ? (
              // Render news items
              currentPosts.map((article, index) => (
                <React.Fragment key={index}>
                  <NewsItem article={article} />
                  {(index + 1) % 2 === 0 && (
                    <div className="my-6 text-center">
                      {/* Optional ad between every 2 articles */}
                      {/* <img
                        src="https://placehold.co/728x90?text=Health+Ad"
                        alt="Ad"
                        className="mx-auto border"
                      /> */}
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              // No results message
              <p className="text-center text-red-500">
                {searchQuery ? "No results found." : "No health news available."}
              </p>
            )}

            {/* Pagination Buttons */}
            <div className="flex justify-center mt-6 space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setCurrentPage(num)}
                  className={`px-3 py-1 border rounded ${num === currentPage ? "bg-black text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  {num}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right Column - Sidebar */}
      <aside className="lg:w-4/12 w-full mt-6 lg:mt-0">
        {/* Ad Banner 300x250 */}
        {/* <AdBanner keyId="e34bc345ee5e3a91f6b935de958d5097" width={300} height={250} delay={3000} /> */}

        {/* Date & Time Widget */}
        <div className="aside-widget p-4 mb-6">
          <div className="widget-header font-bold">Current Time</div>
          <div id="widget-date" className="date"></div> {/* Populated by useEffect */}
          <div id="widget-time" className="time"></div> {/* Populated by useEffect */}
        </div>

        {/* Ad Banner 160x600 for sidebar */}
        {/* <AdBanner keyId="48cc5114b1d06d80c98596ff2e73c514"  width={160} height={600} delay={2000} style={{ marginBottom: "5px" }} mobileHide /> */}

        {/* Optional newsletter subscription */}
        {/* <div className="bg-gray-100 p-4 mb-6">
          <h3 className="text-md font-semibold mb-2">Enter your email address:</h3>
          <input type="email" placeholder="Your email" className="w-full p-2 border rounded mb-2" />
          <button className="bg-gray-600 text-white px-4 py-2 w-full rounded hover:bg-gray-700">Subscribe</button>
          <p className="text-xs mt-2">Delivered by Newstarn</p>
        </div> */}

        {/* Optional trending news */}
        {/* <div className="bg-gray-100 p-4 mb-6">
          <h3 className="text-md font-semibold mb-3">Trending</h3>
          <ul className="list-disc pl-4 text-sm space-y-2">
            {news.slice(0, 6).map((item, index) => (
              <li key={index}>{item.title}</li>
            ))}
          </ul>
        </div> */}

        {/* Optional fixed ad slot */}
        {/* <div className="hidden lg:block sticky top-4">
          <img src="https://placehold.co/800x450?text=Sidebar+Health+Ad" alt="Ad Space" className="w-full h-auto border" />
        </div> */}
      </aside>
    </div>
  );
};

export default Health;
